import { Box, Stack, Typography } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { BackButton } from '../../../components/BackButton';
import { Loader } from '../../../components/Loader/Loader';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { ColumnMeta } from '../../../types';
import { PredefinedColumns } from '../../PortfolioOverview/components/OverviewTable/columns/PredefinedColumns/PredefinedColumns';
import { filterOutUnwantedColumns } from '../../PortfolioOverview/hooks/useColumns/useColumns';
import { columnSort, getGroupMap } from '../../PortfolioOverview/providers/column-utils';
import { columnsMetaState } from '../../PortfolioOverview/state/ViewStateJ';
import { selectedColumnsGridDisplayFieldsOrgState } from '../DataDisplay/DataDisplayState';
import {
  PersistentColumnProvider,
  ColumnSelectionActions,
  GroupColumnMenu,
  RestrictedGroupColumnMenu,
  usePersistentColumnProvider,
  RESTRICTED_COLUMN_GROUPS,
} from '../../../components/ColumnManagement';

// Helper function to check if a group is restricted
const isRestrictedGroup = (groupName: string): boolean => {
  return (
    groupName === RESTRICTED_COLUMN_GROUPS.RUBRICS ||
    groupName === RESTRICTED_COLUMN_GROUPS.FINANCIALS ||
    groupName === RESTRICTED_COLUMN_GROUPS.KPIS
  );
};

function useColumnsDisplay() {
  const columnsMeta = useAtomValue(columnsMetaState(COMPANY_VIEW_TYPE.RETURN_FORECAST));
  const [loading, setLoading] = useState(true);

  const allColumns = useMemo(() => {
    const approvedColumns = filterOutUnwantedColumns(columnsMeta);
    const predefinedColumns = PredefinedColumns.getAllMeta(COMPANY_VIEW_TYPE.RETURN_FORECAST);
    return [...predefinedColumns, ...approvedColumns];
  }, [columnsMeta]);

  const sortedColumns = useMemo(() => {
    return [...allColumns].sort(columnSort);
  }, [allColumns]);

  useEffect(() => {
    setLoading(false);
  }, [sortedColumns]);

  return {
    sortedColumns,
    loading,
  };
}

export function ManageAccessColumnGridDisplayDataLoader() {
  const { sortedColumns, loading } = useColumnsDisplay();
  const [selectedColumns] = useAtom(selectedColumnsGridDisplayFieldsOrgState);

  if (loading) {
    return <Loader />;
  }
  return (
    <PersistentColumnProvider
      initialColumns={selectedColumns}
      persistenceAtom={selectedColumnsGridDisplayFieldsOrgState}
    >
      <ManageAccessColumnGridDisplay columns={sortedColumns} />
    </PersistentColumnProvider>
  );
}

type ManageAccessColumnGridDisplayProps = {
  columns: ColumnMeta[];
};

function ManageAccessColumnGridDisplay({ columns }: ManageAccessColumnGridDisplayProps) {
  const provider = usePersistentColumnProvider();
  const columnsByGroup = useMemo(() => {
    return getGroupMap(columns);
  }, [columns]);

  const sortedGroupNames = useMemo(() => {
    return Array.from(columnsByGroup.keys());
  }, [columnsByGroup]);

  return (
    <Box display='flex' flexDirection='column' gap={2} width='100%'>
      <Box
        display='flex'
        pl='1rem'
        pt='1rem'
        justifyContent='space-between'
        alignItems='flex-start'
        marginBottom={2}
        width='100%'
      >
        <Stack direction='row' gap='0.5rem' alignItems='center'>
          <BackButton
            to={`/${ROUTES.ADMIN_PANEL}/${ROUTES.ADMIN_PANEL_MANAGE_ACCESS}`}
            title='Go back to Manage Access'
          />
          <Box>
            <Typography variant='h4' component='h1' gutterBottom>
              Manage Column Visibility
            </Typography>
          </Box>
        </Stack>
        <ColumnSelectionActionsWrapper />
      </Box>
      <Box pl='1rem' pr='3rem'>
        <Typography variant='body1' gutterBottom>
          Select the columns you want to hide in the grid.
        </Typography>
        <Typography variant='body1' gutterBottom>
          Available Columns
        </Typography>
        {sortedGroupNames.map((group) => {
          const columnsInGroup = columnsByGroup.get(group) ?? [];

          if (columnsInGroup.length === 0) {
            return null;
          }

          if (isRestrictedGroup(group)) {
            return (
              <RestrictedGroupColumnMenu
                key={group}
                groupName={group}
                columns={columnsInGroup}
                provider={provider}
                checkboxVariant={'critical'}
              />
            );
          }

          return (
            <GroupColumnMenu
              key={group}
              groupName={group}
              columns={columnsInGroup}
              provider={provider}
              showMenuList={true}
              checkboxVariant={'critical'}
            />
          );
        })}
      </Box>
    </Box>
  );
}

function ColumnSelectionActionsWrapper() {
  const provider = usePersistentColumnProvider();

  return <ColumnSelectionActions provider={provider} />;
}
